/**
 * What the student currently holds.
 *
 * Ported from the app's `widgets/subscription_card.dart`, including the
 * reasoning that was paid for in production bugs.
 *
 * *** EVERY DECISION HERE IS THE SERVER'S ***
 * Whether a plan is live, why it is not, whether buying is allowed, and what
 * to say about a payment in flight all arrive computed. This renders them.
 * The app inferred two of these once and got both wrong — see `badge()`.
 */

import { Button } from '@/components/ui/Button'
import { Pill } from '@/components/ui/Pill'
import { formatDate, formatMoney } from '@/lib/format'
import type { CurrentPlan, MySubscription } from '@/types/api'
import styles from './PlansPage.module.css'

/**
 * The status badge.
 *
 * *** THE SERVER SAYS WHY A PLAN IS DEAD — THIS DOES NOT GUESS ***
 * Two of these branches used to read `sessions_remaining > 0` as "expired"
 * and anything else as "used up". Both are wrong for a DECLINED purchase,
 * which leaves a dormant subscription with every session intact: a student
 * who had never paid was shown "Plan expired" (reported 2026-09-19 against
 * the app). `inactive_reason` is the server's own answer.
 *
 * The app also carries a numeric fallback for a server older than the build.
 * **Deliberately not ported** — the portal is served by the same deploy as
 * its API, so that case cannot arise here, and the fallback is wrong in
 * exactly the dormant case above.
 */
function badge(sub: MySubscription): { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' } {
  if (sub.is_live) return { label: 'Active', tone: 'success' }

  /*
    Asked for, not yet confirmed. Not an error — and offering a second
    purchase here would create a second charge.

    *** WHO IS BEING WAITED ON DIFFERS BY CLIENT ***
    On a gateway client the student waits for their own payment to clear. On
    a cash client they have to go and pay a person, and "Payment pending"
    would leave them waiting for something that never happens on its own.
  */
  if (!sub.can_purchase) {
    return {
      label:
        sub.payment_mode === 'CASH' ? 'Awaiting approval' : 'Payment pending',
      tone: 'warning',
    }
  }

  switch (sub.inactive_reason) {
    case 'EXPIRED':
      return { label: 'Plan expired', tone: 'danger' }
    case 'NO_SESSIONS':
      // "Sessions used up" read as a reprimand. This states the balance,
      // which is what the student needs in order to act.
      return { label: 'No sessions left', tone: 'danger' }
    case 'CANCELLED':
      return { label: 'Plan cancelled', tone: 'danger' }
    case 'NOT_COMPLETED':
      // A purchase that never completed. NOT an expiry, and not a plan the
      // student ever held.
      return { label: 'Purchase incomplete', tone: 'danger' }
    default:
      return { label: 'No plan', tone: 'neutral' }
  }
}

/**
 * A plan the student holds, when the headline card is describing something
 * else — an abandoned purchase, usually.
 *
 * *** THIS IS NOT A DUPLICATE OF THE CARD BELOW ***
 * The server sends `current_plan` only when it is a DIFFERENT subscription
 * from the one the main fields describe. Both on screen at once is the
 * point: "your last purchase failed" and "you still hold this until the
 * 2nd" are both true and the student needs both.
 */
export function CurrentPlanCard({ plan }: { plan: CurrentPlan }) {
  return (
    <section className={styles.mine}>
      <header className={styles.mineHead}>
        <div>
          <span className={styles.mineLabel}>Your plan</span>
          <h2 className={styles.mineName}>{plan.plan_name ?? 'Your plan'}</h2>
        </div>
        {/*
          *** "ACTIVE" WOULD BE A LIE ON A SPENT PLAN ***
          `is_live` is false once the sessions are gone, even though the term
          is still running — which is exactly the case this card exists for.
          Two different states, two different badges.
        */}
        <Pill tone={plan.is_live ? 'success' : 'warning'}>
          {plan.is_live ? 'Active' : 'No sessions left'}
        </Pill>
      </header>

      <div className={styles.stats}>
        <Stat value={plan.sessions_remaining} label="Left" />
        <Stat value={plan.sessions_used} label="Used" />
        <Stat value={plan.sessions_held} label="Booked" />
        <Stat value={plan.session_count} label="Total" />
      </div>

      {plan.expires_at && (
        <p className={styles.mineExpiry}>
          Valid until {formatDate(plan.expires_at)}
        </p>
      )}
    </section>
  )
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  )
}

export function SubscriptionCard({
  sub,
  onBuy,
}: {
  sub: MySubscription
  /** Null hides the button — used while a purchase is already in flight. */
  onBuy: (() => void) | null
}) {
  const { label, tone } = badge(sub)
  const hasPlan = Boolean(sub.plan_name)

  /*
    *** A PURCHASE THAT NEVER COMPLETED IS NOT A PLAN THEY HOLD ***
    The server still reports the dormant row's figures — 2 sessions, valid
    until the 5th — because those are what WOULD have been bought. Rendering
    them as a balance tells a student who paid nothing that they own two
    sessions and have until Tuesday to use them (reported 2026-10-31, after
    abandoning the bank's page).

    `NOT_COMPLETED` and `CANCELLED` are both "this never became yours".
    EXPIRED and NO_SESSIONS are real history and keep their numbers: a
    student who used two sessions should see that they used two.
  */
  const neverOwned =
    sub.inactive_reason === 'NOT_COMPLETED' ||
    sub.inactive_reason === 'CANCELLED'

  return (
    <section className={styles.mine}>
      <header className={styles.mineHead}>
        <div>
          {/* "Your plan" over a purchase that never completed claims
              ownership of something nobody paid for. */}
          <span className={styles.mineLabel}>
            {neverOwned ? 'Last attempt' : 'Your plan'}
          </span>
          <h2 className={styles.mineName}>
            {sub.plan_name ?? 'No plan yet'}
          </h2>
        </div>
        <Pill tone={tone}>{label}</Pill>
      </header>

      {/*
        *** THE SERVER'S OWN SENTENCE, NOT OURS ***
        It distinguishes "never had a plan" from "used it all" from "your
        payment was declined — no money was taken", and the wording tracks
        gateway vs cash. Rewriting it here would put those apart again.
      */}
      {sub.message && <p className={styles.mineMessage}>{sub.message}</p>}

      {/*
        *** THE ONE THING THE SERVER'S SENTENCE DOES NOT SAY ***
        "Your last purchase was not completed" is accurate but leaves the
        open question: was I charged? Someone who just came back from a bank
        page needs that answered before anything else, and the server only
        says it on the FAILED branch of its own message.

        Safe to state: a payment that reached FAILED or was never completed
        took nothing — the server cancels the dormant subscription precisely
        because nothing was collected.
      */}
      {neverOwned && (
        <p className={styles.mineReassure}>
          No money was taken. You can buy a plan again below.
        </p>
      )}

      {hasPlan && !neverOwned && (
        <div className={styles.stats}>
          <Stat value={sub.sessions_remaining ?? 0} label="Left" />
          <Stat value={sub.sessions_used ?? 0} label="Used" />
          {/* Reserved by upcoming bookings. Shown because it explains a
              balance that looks lower than expected. */}
          <Stat value={sub.sessions_held ?? 0} label="Booked" />
          <Stat value={sub.session_count ?? 0} label="Total" />
        </div>
      )}

      {/*
        *** "EXPIRED ON" IS ONLY TRUE WHEN IT EXPIRED ***
        This read `is_live ? 'Valid until' : 'Expired on'`, and a plan can be
        not-live for four different reasons. A student who had SPENT their
        two sessions was shown "Expired on Nov 02, 2026" — a future date,
        under a badge correctly saying "No sessions left" (reported
        2026-10-31). Two labels, two different stories, one of them false.

        The server already decided which: `inactive_reason === 'EXPIRED'`.
        Anything else still has time on it, however unusable it is.
      */}
      {sub.expires_at && !neverOwned && (
        <p className={styles.mineExpiry}>
          {sub.inactive_reason === 'EXPIRED' ? 'Expired on' : 'Valid until'}{' '}
          {formatDate(sub.expires_at)}
        </p>
      )}

      {/*
        *** `can_purchase` IS THE ONLY GATE ***
        True only when nothing usable is held. Showing Buy against anything
        else offers a button the server would refuse — and on a dormant
        purchase it would take a second payment for a plan already bought.
      */}
      {sub.can_purchase && onBuy && (
        <div className={styles.mineAction}>
          <Button variant="primary" onClick={onBuy}>
            {hasPlan ? 'Buy a plan' : 'Choose a plan'}
          </Button>
        </div>
      )}
    </section>
  )
}

/**
 * A plan a student can buy.
 *
 * Price shows `total_amount`, never `price`: on a tenant with fee heads the
 * two differ, and a student who agrees to one figure and is charged another
 * has been misled — on a cash client they turn up at the counter with the
 * wrong money.
 */
export function PlanCard({
  plan,
  disabled,
  busy,
  onBuy,
}: {
  plan: import('@/types/api').PublicPlan
  disabled: boolean
  busy: boolean
  onBuy: () => void
}) {
  const hasFees = plan.line_items.length > 1

  return (
    <article className={styles.plan}>
      <header className={styles.planHead}>
        <h3 className={styles.planName}>{plan.name}</h3>
        <span className={styles.planPrice}>
          {formatMoney(plan.total_amount, plan.currency)}
        </span>
      </header>

      {plan.description && (
        <p className={styles.planDesc}>{plan.description}</p>
      )}

      <ul className={styles.planFacts}>
        <li>
          <strong>{plan.session_count}</strong>{' '}
          {plan.session_count === 1 ? 'session' : 'sessions'}
        </li>
        <li>
          {plan.validity_days
            ? `Valid ${plan.validity_days} days`
            : 'No expiry'}
        </li>
      </ul>

      {/*
        "Why is it more than the plan price?" answered on the screen rather
        than at the counter. Only when fees actually apply.
      */}
      {hasFees && (
        <ul className={styles.breakdown}>
          {plan.line_items.map((li, i) => (
            <li key={`${li.label}-${i}`}>
              <span>{li.label}</span>
              <span>{formatMoney(li.amount, plan.currency)}</span>
            </li>
          ))}
        </ul>
      )}

      <Button
        variant="primary"
        fullWidth
        onClick={onBuy}
        disabled={disabled}
        loading={busy}
      >
        Buy this plan
      </Button>
    </article>
  )
}
